import FeatureGates, { PerimeterType } from '@atlaskit/feature-gate-js-client';
import FeatureGateSingleFetchProvider from '@atlaskit/feature-gate-single-fetch-provider';
import { ConfigClient } from '@atlaskit/web-config-client';

import { isFedRamp } from '@atlassian/atl-context';

import type { SessionDataType } from '@confluence/session-data';
import { getMonitoringClient } from '@confluence/monitoring';

import { getStatsigEnvironment } from './getStatsigEnvironment';
import StatsigConfigurations from './StatsigConfigurations';

type StatsigOptions = {
	userId: SessionDataType['userId'];
	cloudId: SessionDataType['cloudId'] | null;
	environment: SessionDataType['environment'] | string;
};

export async function initializeClientWithProvider({
	cloudId,
	environment,
	userId,
}: StatsigOptions) {
	// https://bitbucket.org/atlassian/feature-flag-service/src/a6adccc26a95818c8128c63e4eb817414793b3c2/src/main/resources/application.yml#lines-386
	const TARGET_APP = 'confluence_web';

	try {
		const provider = new FeatureGateSingleFetchProvider({
			apiKey: getFeatureFlagsServiceApiKey(environment),
		});

		await FeatureGates.initializeWithProvider(
			{
				environment: getStatsigEnvironment(environment),
				targetApp: TARGET_APP,
				perimeter: isFedRamp() ? PerimeterType.FEDRAMP_MODERATE : PerimeterType.COMMERCIAL,
			},
			provider,
			{
				atlassianAccountId: userId || undefined,
				tenantId: cloudId || undefined,
			},
		);

		// StatsigConfigurations reads FeatureGates this must be initialized after FeatureGates
		StatsigConfigurations.initialize(
			await ConfigClient.fetch({
				// Base URL of the feature-flag-service
				ffsBaseUrl: '/gateway/api/flags',
				ffsApiKey: getFeatureFlagsServiceApiKey(environment),
				// User context to evaluate the configuration against
				context: {
					namespace: TARGET_APP,
					identifiers: {
						atlassianAccountId: userId || undefined,
						tenantId: cloudId || undefined,
					},
					metadata: {},
				},
			}),
		);
	} catch (error: unknown) {
		if (error instanceof Error) {
			getMonitoringClient().submitError(error, {
				attribution: 'statsig',
			});
		}
	}
}

function getFeatureFlagsServiceApiKey(confluenceEnvironment: StatsigOptions['environment']) {
	// see http://go/fx3/resources/api-keys
	switch (confluenceEnvironment) {
		case 'PRODUCTION':
		case 'prod':
			return isFedRamp()
				? 'bfa7d1a5-f85f-45ae-8dd1-ce35820db2ca'
				: '5f23756f-37cc-4259-8261-3e4e081559c8';
		case 'STAGING':
		case 'staging':
			return isFedRamp()
				? '12825e24-5928-40bf-98d8-ad76ac07060a'
				: '48512e86-2695-4b50-a4c5-da6bb1bcd591';
		case 'DEVELOPMENT':
		default:
			return 'f10b0994-2e84-4eb9-a02a-ac9cb34c954d';
	}
}
